// @ts-nocheck
import React, { useEffect, useState, useMemo } from "react";
import { GlobalAdmin } from "coolremote-sdk";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import useStyles from "../DaikinConsistency.style";
import { SectionedIssuesTable } from "./ConsistencyLineView";

const SECTION_LABELS = {
  system:  "System",
  control: "Indoor",
  service: "Service",
  outdoor: "Outdoor",
};
const SECTION_ORDER = ["outdoor", "service", "control", "system"];
const TYPE_LABEL    = { control: "IDU", service: "SRV", outdoor: "ODU" };

// ── helpers ──────────────────────────────────────────────────────────────────

function diffHasChanges(diff) {
  if (!diff) { return false; }
  for (const unitType of ["control", "service", "outdoor"]) {
    if ((diff.newlyDisconn?.[unitType]  || 0) > 0) { return true; }
    if ((diff.reconnected?.[unitType]   || 0) > 0) { return true; }
    if ((diff.newUnits?.[unitType]      || 0) > 0) { return true; }
    if ((diff.removedUnits?.[unitType]  || 0) > 0) { return true; }
  }
  if ((diff.newNonDisconnCount      || 0) > 0) { return true; }
  if ((diff.resolvedNonDisconnCount || 0) > 0) { return true; }
  return false;
}

function toSections(issues) {
  const bySection = {};
  for (const issue of issues || []) {
    const sectionKey = issue.section;
    if (!bySection[sectionKey]) { bySection[sectionKey] = {}; }
    const unitKey = issue.unitId ? String(issue.unitId) : (issue.address || issue.name || "_");
    if (!bySection[sectionKey][unitKey]) {
      bySection[sectionKey][unitKey] = { name: issue.name, address: issue.address, issues: [] };
    }
    bySection[sectionKey][unitKey].issues.push({ priority: issue.priority, description: issue.description });
  }
  return SECTION_ORDER
    .filter((sectionKey) => bySection[sectionKey])
    .map((sectionKey) => ({
      label: SECTION_LABELS[sectionKey] || sectionKey,
      units: Object.values(bySection[sectionKey]),
    }));
}

function flattenLinesIssues(reportDevice) {
  return Object.values(reportDevice?.lines || {}).flatMap((line: any) => line.issues || []);
}

function effectiveIssueGroups(reportDevice) {
  const storedGroups = reportDevice?.issueGroups;
  if (storedGroups && Object.values(storedGroups).some((section: any) => section && Object.keys(section).length > 0)) {
    return storedGroups;
  }
  // fallback: merge from per-line issueGroups for old reports
  const merged: any = { control: {}, service: {}, outdoor: {}, system: {} };
  for (const line of Object.values(reportDevice?.lines || {}) as any[]) {
    for (const [sectionKey, group] of Object.entries(line.issueGroups || {}) as any[]) {
      if (!merged[sectionKey]) { merged[sectionKey] = {}; }
      for (const [description, unitIds] of Object.entries(group || {}) as any[]) {
        if (!merged[sectionKey][description]) { merged[sectionKey][description] = []; }
        merged[sectionKey][description].push(...(unitIds || []));
      }
    }
  }
  return merged;
}

// ── small components ──────────────────────────────────────────────────────────

function buildDiffChips(diff): { label: string; color: any }[] {
  if (!diff) { return []; }
  const chips: { label: string; color: any }[] = [];
  for (const unitType of ["control", "service", "outdoor"]) {
    const typeLabel = TYPE_LABEL[unitType];
    if ((diff.reconnected?.[unitType]   || 0) > 0) { chips.push({ label: `${diff.reconnected[unitType]} ${typeLabel} reconnected`,   color: "success" }); }
    if ((diff.newlyDisconn?.[unitType]  || 0) > 0) { chips.push({ label: `${diff.newlyDisconn[unitType]} ${typeLabel} disconnected`,  color: "error"   }); }
    if ((diff.newUnits?.[unitType]      || 0) > 0) { chips.push({ label: `+${diff.newUnits[unitType]} new ${typeLabel}`,              color: "info"    }); }
    if ((diff.removedUnits?.[unitType]  || 0) > 0) { chips.push({ label: `-${diff.removedUnits[unitType]} ${typeLabel} removed`,      color: "error"   }); }
  }
  if ((diff.resolvedNonDisconnCount || 0) > 0) { chips.push({ label: `${diff.resolvedNonDisconnCount} resolved`,  color: "success" }); }
  if ((diff.newNonDisconnCount      || 0) > 0) { chips.push({ label: `+${diff.newNonDisconnCount} new issues`,   color: "warning" }); }
  return chips;
}

const DIFF_KIND_CONFIG = {
  added:        { symbol: "+", color: "info.main"    },
  removed:      { symbol: "−", color: "error.main"   },
  disconnected: { symbol: "↓", color: "error.main"   },
  reconnected:  { symbol: "↑", color: "success.main" },
  resolved:     { symbol: "✓", color: "success.main" },
  "new issue":  { symbol: "!", color: "warning.main" },
};

const SECTION_FULL: Record<string, string> = {
  control: "Indoor",
  service: "Service",
  outdoor: "Outdoor",
  system:  "System",
};

function DiffUnitLabel({ name, address, type, lineId }: any) {
  const classes    = useStyles();
  const displayId  = name || address || null;
  const sectionName = SECTION_FULL[type] || type;
  return (
    <Box component="span" className={classes.diffUnitLabelWrap}>
      {displayId && (
        <Typography component="span" sx={{ fontSize: "0.8rem", color: "text.primary" }}>
          {displayId}
        </Typography>
      )}
      <Typography component="span" sx={{ fontSize: "0.7rem", color: "text.disabled" }}>
        {sectionName}
      </Typography>
    </Box>
  );
}

function DiffTable({ rows }: { rows: { kind: string; left: React.ReactNode; right?: string }[] }) {
  const classes = useStyles();
  if (!rows.length) { return null; }
  return (
    <TableContainer component={Box} className={classes.diffTableContainer}>
      <Table size="small">
        <TableBody>
          {rows.map((row, index) => {
            const kindConfig = DIFF_KIND_CONFIG[row.kind] || { symbol: "?", color: "text.secondary" };
            return (
              <TableRow key={index} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                <TableCell className={classes.diffTableSymbolCell} sx={{ color: kindConfig.color }}>
                  {kindConfig.symbol}
                </TableCell>
                <TableCell sx={{ py: 0.55 }}>{row.left}</TableCell>
                {row.right !== undefined && (
                  <TableCell sx={{ py: 0.55, fontSize: "0.78rem", color: "text.secondary", maxWidth: 240 }}>
                    {row.right}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DeviceDiffSummary({ diff }) {
  if (!diff) { return null; }

  if (diff.unitChanges || diff.resolvedIssues || diff.newIssues) {
    const unitRows = (diff.unitChanges || []).map((unitChange) => ({
      kind: unitChange.kind,
      left: <DiffUnitLabel name={unitChange.name} address={unitChange.address} type={unitChange.type} lineId={unitChange.lineId} />,
    }));
    const issueRows = [
      ...(diff.resolvedIssues || []).map((issue) => ({
        kind: "resolved",
        left: <DiffUnitLabel name={issue.name} address={issue.address} type={issue.section} lineId={issue.lineId} />,
        right: issue.description,
      })),
      ...(diff.newIssues || []).map((issue) => ({
        kind: "new issue",
        left: <DiffUnitLabel name={issue.name} address={issue.address} type={issue.section} lineId={issue.lineId} />,
        right: issue.description,
      })),
    ];
    const allRows = [...unitRows, ...issueRows];
    if (!allRows.length) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          No changes vs previous snapshot
        </Typography>
      );
    }
    return <Box sx={{ mb: 1.5 }}><DiffTable rows={allRows} /></Box>;
  }

  // Old diff format fallback (counts only)
  const chips = buildDiffChips(diff);
  if (!chips.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        No changes vs previous snapshot
      </Typography>
    );
  }
  return (
    <Box sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {chips.map((chip, index) => (
        <Chip key={index} label={chip.label} size="small" color={chip.color} variant="outlined"
              className={useStyles().chipMd} />
      ))}
    </Box>
  );
}

function IssueGroupsSummary({ issueGroups }) {
  const classes = useStyles();
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  const toggleSection = (sectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(sectionKey) ? next.delete(sectionKey) : next.add(sectionKey);
      return next;
    });
  };

  const sections = ["outdoor", "service", "control", "system"]
    .map((sectionKey) => {
      const group = issueGroups?.[sectionKey];
      if (!group) { return null; }
      const items = Object.entries(group)
        .filter(([, unitIds]) => (unitIds as any[]).length > 0)
        .map(([description, unitIds]) => ({ description, count: (unitIds as any[]).length }))
        .sort((a, b) => b.count - a.count);
      if (!items.length) { return null; }
      return { sectionKey, label: SECTION_FULL[sectionKey] || sectionKey, items };
    })
    .filter(Boolean);

  if (!sections.length) { return null; }

  return (
    <TableContainer component={Box} className={classes.issueGroupsContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeaderCell}>Issue</TableCell>
            <TableCell align="right" className={classes.tableHeaderCell}>Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map(({ sectionKey, label, items }) => {
            const isCollapsed = collapsedSections.has(sectionKey);
            return (
              <React.Fragment key={sectionKey}>
                <TableRow className={classes.issueGroupSectionRow} onClick={() => toggleSection(sectionKey)}>
                  <TableCell
                    colSpan={2}
                    className={classes.issueGroupSectionCell}
                    sx={{ borderBottom: isCollapsed ? 0 : undefined }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <IconButton size="small" sx={{ p: 0, mr: 0.25 }} tabIndex={-1}>
                        {isCollapsed
                          ? <KeyboardArrowUpIcon sx={{ fontSize: 15 }} />
                          : <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                      {label}
                    </Box>
                  </TableCell>
                </TableRow>
                {!isCollapsed && items.map((item, index) => (
                  <TableRow key={index} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell className={classes.issueGroupItemDesc}>{item.description}</TableCell>
                    <TableCell align="right" className={classes.issueGroupItemCount}>{item.count}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function UnitCountRow({ totals }: any) {
  const classes         = useStyles();
  const unitsByType     = totals?.unitsByType;
  const disconnByType   = totals?.disconnectedByType;
  const systemsCount    = totals?.systemsCount || 0;
  if (!unitsByType || (!unitsByType.control && !unitsByType.service && !unitsByType.outdoor && !systemsCount)) {
    return null;
  }
  const parts = [
    unitsByType.control ? { label: "Indoor",  count: unitsByType.control, offlineCount: disconnByType?.control || 0 } : null,
    unitsByType.service ? { label: "Service", count: unitsByType.service, offlineCount: disconnByType?.service || 0 } : null,
    unitsByType.outdoor ? { label: "Outdoor", count: unitsByType.outdoor, offlineCount: disconnByType?.outdoor || 0 } : null,
    systemsCount        ? { label: "System",  count: systemsCount,        offlineCount: 0                           } : null,
  ].filter(Boolean);
  return (
    <Box className={classes.unitCountBar}>
      {parts.map((part: any) => (
        <Box key={part.label} className={classes.unitCountItem}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1 }}>{part.count}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>{part.label}</Typography>
          {part.offlineCount > 0 && (
            <Typography sx={{ fontSize: "0.72rem", color: "error.main" }}>({part.offlineCount} off)</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function TotalsChips({ totals, classes }) {
  if (!totals) { return null; }
  return (
    <Box sx={{ display: "inline-flex", gap: 0.5 }}>
      {totals.high   > 0 && <Chip label={`${totals.high} high`}   size="small" color="error"   className={classes.chipMd} />}
      {totals.medium > 0 && <Chip label={`${totals.medium} med`}  size="small" color="warning" className={classes.chipMd} />}
      {totals.totalIssues === 0 && !totals.disconnected && (
        <Chip label="clean" size="small" color="success" className={classes.chipMd} />
      )}
    </Box>
  );
}

function SectionLabel({ children }) {
  const classes = useStyles();
  return (
    <Typography className={classes.sectionLabelCaption}>
      {children}
    </Typography>
  );
}

function IssuesDialog({ open, onClose, serial, totals, issues, issueGroups, deviceOffline, diff, isLoading, classes }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box className={classes.dialogTitleRow}>
          <Typography fontWeight={700} className={classes.monoText}>{serial}</Typography>
          {deviceOffline && (
            <Chip label="offline at snapshot" size="small" color="warning" variant="outlined" className={classes.chipMd} />
          )}
          {!isLoading && <TotalsChips totals={totals} classes={classes} />}
          <IconButton size="small" onClick={onClose} sx={{ ml: "auto" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5, height: "min(900px, 75vh)", overflow: "auto" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <UnitCountRow totals={totals} />
            {diff && (
              <Box sx={{ mt: 2.5 }}>
                <SectionLabel>Changes</SectionLabel>
                <DeviceDiffSummary diff={diff} />
              </Box>
            )}
            <Box sx={{ mt: 2.5 }}>
              <SectionLabel>Issue Summary</SectionLabel>
              <IssueGroupsSummary issueGroups={issueGroups} />
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <SectionLabel>Details</SectionLabel>
              <SectionedIssuesTable sections={toSections(issues)} emptyMessage="No issues found." />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CustomerIssuesDialog({ open, onClose, customerName, report, classes }) {
  const devicesWithIssues = (report?.devices || []).filter((device) => {
    if ((device.totals?.totalIssues || 0) > 0) { return true; }
    if ((device.totals?.disconnected || 0) > 0) { return true; }
    if (device.issues?.length || flattenLinesIssues(device).length) { return true; }
    return false;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box className={classes.dialogTitleRow}>
          <Typography fontWeight={700}>{customerName}</Typography>
          <IconButton size="small" onClick={onClose} sx={{ ml: "auto" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5, height: "min(900px, 75vh)", overflow: "auto" }}>
        {devicesWithIssues.length === 0 ? (
          <Alert severity="success">All devices are clean.</Alert>
        ) : (
          devicesWithIssues.map((device) => (
            <Box key={device.serial} className={classes.deviceCard}>
              <Box className={classes.deviceCardHeader}>
                <Typography fontWeight={700} sx={{ fontSize: "0.78rem" }} className={classes.monoText}>
                  {device.serial}
                </Typography>
                {device.deviceOffline && (
                  <Chip label="offline" size="small" color="warning" variant="outlined" className={classes.chipMd} />
                )}
                <Box sx={{ ml: "auto" }}>
                  <TotalsChips totals={device.totals} classes={classes} />
                </Box>
              </Box>
              <UnitCountRow totals={device.totals} />
              {device.diff && (
                <Box sx={{ mt: 2 }}>
                  <SectionLabel>Changes</SectionLabel>
                  <DeviceDiffSummary diff={device.diff} />
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <SectionLabel>Issue Summary</SectionLabel>
                <IssueGroupsSummary issueGroups={effectiveIssueGroups(device)} />
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeviceRow({ dev, result, isLoading, onRun, reportDev, classes }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [liveOpen,   setLiveOpen]   = useState(false);

  const reportIssues    = reportDev?.issues?.length ? reportDev.issues : flattenLinesIssues(reportDev);
  const hasReportIssues = !!reportDev && reportIssues.length > 0;
  const hasLiveResult   = result !== undefined && result !== null;

  const handleRun = () => {
    setLiveOpen(true);
    onRun(dev.serial);
  };

  return (
    <>
      <TableRow hover className={dev.isConnected === false ? classes.rowDisconnected : undefined}>
        <TableCell className={classes.tdMono} sx={{ pl: 4, fontWeight: 600 }}>
          {dev.serial}
        </TableCell>
        <TableCell className={classes.td} sx={{ color: "text.secondary" }}>
          {dev.site || "—"}
        </TableCell>
        <TableCell className={classes.td}>
          {reportDev && (
            reportDev.diff === undefined
              ? <Chip label="no prev snapshot" size="small" variant="outlined" className={classes.chipMd} sx={{ mr: 0.5, color: "text.disabled", borderColor: "divider" }} />
              : diffHasChanges(reportDev.diff)
                ? <Chip label="changes"    size="small" color="warning" variant="outlined" className={classes.chipMd} sx={{ mr: 0.5 }} />
                : <Chip label="no changes" size="small" color="success" variant="outlined" className={classes.chipMd} sx={{ mr: 0.5 }} />
          )}
          {reportDev && <TotalsChips totals={reportDev.totals} classes={classes} />}
        </TableCell>
        <TableCell className={classes.td} sx={{ p: 0, pr: 0.5, textAlign: "right", whiteSpace: "nowrap" }}>
          <Tooltip title={hasReportIssues ? "View report issues" : reportDev ? "No issues in report" : "Load a report first"}>
            <span>
              <IconButton
                size="small"
                disabled={!hasReportIssues}
                sx={{ opacity: hasReportIssues ? 0.55 : 0.2, "&:hover": { opacity: hasReportIssues ? 1 : 0.2 } }}
                onClick={() => setReportOpen(true)}
              >
                <FormatListBulletedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={hasLiveResult ? "Re-run live analysis" : "Run live analysis"}>
            <span>
              <IconButton
                size="small"
                disabled={isLoading}
                sx={{ opacity: 0.65, "&:hover": { opacity: 1 } }}
                onClick={handleRun}
              >
                {isLoading ? (
                  <CircularProgress size={14} />
                ) : hasLiveResult ? (
                  <RefreshIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      {hasReportIssues && (
        <IssuesDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          serial={dev.serial}
          totals={reportDev.totals}
          issues={reportIssues}
          issueGroups={effectiveIssueGroups(reportDev)}
          deviceOffline={reportDev?.deviceOffline}
          diff={reportDev?.diff}
          classes={classes}
        />
      )}

      <IssuesDialog
        open={liveOpen}
        onClose={() => setLiveOpen(false)}
        serial={dev.serial}
        totals={result?.totals}
        issues={result?.issues || []}
        issueGroups={result?.issueGroups}
        deviceOffline={false}
        diff={null}
        isLoading={isLoading}
        classes={classes}
      />
    </>
  );
}

function CustomerRows({
  customer,
  selectedDate,
  analysisResults,
  analysisLoading,
  onRun,
  reportResults,
  reportLoading,
  onLoadReport,
  classes,
}) {
  const [collapsed, setCollapsed]               = useState(false);
  const [customerIssuesOpen, setCustomerIssuesOpen] = useState(false);

  const report       = reportResults[customer.id];
  const hasReport    = report !== undefined;
  const reportDevMap = Object.fromEntries((report?.devices || []).map((device) => [device.serial, device]));

  const hasAnyReportIssues = (report?.devices || []).some((device) => {
    if ((device.issues?.length || 0) > 0) { return true; }
    return flattenLinesIssues(device).length > 0;
  });

  const diffChipLabel = (() => {
    if (!report) { return null; }
    const devices    = report.devices || [];
    const hasDiffData = devices.some((device) => device.diff !== undefined);
    if (!hasDiffData) { return "no prev snapshot"; }
    return devices.some((device) => diffHasChanges(device.diff)) ? "changes" : "no changes";
  })();

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  return (
    <>
      <TableRow
        className={classes.rowSystemHeader}
        sx={{ cursor: "pointer", bgcolor: "action.selected" }}
        onClick={toggleCollapsed}
      >
        <TableCell className={classes.td} sx={{ py: "8px", px: "12px" }}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton size="small" sx={{ p: 0.25 }} onClick={(event) => { event.stopPropagation(); toggleCollapsed(); }}>
              {collapsed
                ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            </IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{customer.name}</Typography>
            <Chip
              label={customer.devices?.length || 0}
              size="small"
              variant="outlined"
              className={classes.chipSm}
              sx={{ color: "text.secondary", borderColor: "divider" }}
            />
          </Box>
        </TableCell>
        <TableCell className={classes.td} sx={{ py: "8px" }} />
        <TableCell className={classes.td} sx={{ py: "8px" }}>
          {hasReport && report && (
            <Box className={classes.statusChipRow}>
              {diffChipLabel === "no prev snapshot" && (
                <Chip label="no prev snapshot" size="small" variant="outlined" className={classes.chipMd} sx={{ color: "text.disabled", borderColor: "divider" }} />
              )}
              {diffChipLabel === "changes" && (
                <Chip label="changes"    size="small" color="warning" variant="outlined" className={classes.chipMd} />
              )}
              {diffChipLabel === "no changes" && (
                <Chip label="no changes" size="small" color="success" variant="outlined" className={classes.chipMd} />
              )}
              <TotalsChips totals={report.totals} classes={classes} />
            </Box>
          )}
          {hasReport && !report && (
            <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic", fontSize: "0.65rem" }}>
              no report
            </Typography>
          )}
        </TableCell>
        <TableCell className={classes.td} sx={{ py: "8px", px: 0, pr: 0.5, textAlign: "right", whiteSpace: "nowrap" }}>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            {hasReport && report && (
              <Tooltip title={hasAnyReportIssues ? "View all customer issues" : "No issues in report"}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!hasAnyReportIssues}
                    sx={{ opacity: hasAnyReportIssues ? 0.55 : 0.25, "&:hover": { opacity: hasAnyReportIssues ? 1 : 0.25 } }}
                    onClick={(event) => { event.stopPropagation(); setCustomerIssuesOpen(true); }}
                  >
                    <FormatListBulletedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={!customer.devices?.length ? "No devices for this customer" : `Load report for ${selectedDate}`}>
              <span>
                <IconButton
                  size="small"
                  sx={{ opacity: 0.55, "&:hover": { opacity: customer.devices?.length ? 1 : 0.55 } }}
                  disabled={!!reportLoading[customer.id] || !customer.devices?.length}
                  onClick={(event) => { event.stopPropagation(); onLoadReport(customer.id); }}
                >
                  {reportLoading[customer.id] ? (
                    <CircularProgress size={14} />
                  ) : hasReport ? (
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <AssessmentIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {!collapsed && (customer.devices || []).map((device) => (
        <DeviceRow
          key={device.serial}
          dev={device}
          result={analysisResults[device.serial]}
          isLoading={!!analysisLoading[device.serial]}
          onRun={onRun}
          reportDev={reportDevMap[device.serial]}
          classes={classes}
        />
      ))}

      {hasReport && report && (
        <CustomerIssuesDialog
          open={customerIssuesOpen}
          onClose={() => setCustomerIssuesOpen(false)}
          customerName={customer.name}
          report={report}
          classes={classes}
        />
      )}
    </>
  );
}

export default function CustomerReportsTab() {
  const classes = useStyles();
  const [customers,       setCustomers]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [filter,          setFilter]          = useState("");
  const [selectedDate,    setSelectedDate]    = useState(new Date().toISOString().slice(0, 10));
  const [analysisResults, setAnalysisResults] = useState({});
  const [analysisLoading, setAnalysisLoading] = useState({});
  const [reportResults,   setReportResults]   = useState({});
  const [reportLoading,   setReportLoading]   = useState({});

  const loadCustomers = () => {
    setLoading(true);
    setError(null);
    GlobalAdmin.getDaikinReportCustomers()
      .then((response) => setCustomers(response?.data || response || []))
      .catch((error)   => setError(error?.message || "Failed to load customers"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadCustomers(); }, []);
  useEffect(() => { setReportResults({}); }, [selectedDate]);

  const filteredCustomers = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) { return customers; }
    return customers
      .map((customer) => {
        if (customer.name?.toLowerCase().includes(query)) { return customer; }
        const matchingDevices = (customer.devices || []).filter(
          (device) => device.serial?.toLowerCase().includes(query) || device.site?.toLowerCase().includes(query)
        );
        return matchingDevices.length ? { ...customer, devices: matchingDevices } : null;
      })
      .filter(Boolean);
  }, [customers, filter]);

  const runAnalysis = async (serial) => {
    setAnalysisLoading((prev) => ({ ...prev, [serial]: true }));
    setAnalysisResults((prev) => ({ ...prev, [serial]: null }));
    try {
      const response = await GlobalAdmin.getDaikinDeviceAnalysis(serial);
      setAnalysisResults((prev) => ({ ...prev, [serial]: response?.data || response }));
    } catch (error) {
      setError(`Analysis failed for ${serial}: ${error?.message}`);
    } finally {
      setAnalysisLoading((prev) => ({ ...prev, [serial]: false }));
    }
  };

  const loadReport = async (customerId) => {
    setReportLoading((prev) => ({ ...prev, [customerId]: true }));
    setReportResults((prev) => ({ ...prev, [customerId]: undefined }));
    try {
      const dateTs   = new Date(selectedDate + "T00:00:00Z").getTime();
      const response = await GlobalAdmin.getDaikinReport(customerId, dateTs);
      setReportResults((prev) => ({ ...prev, [customerId]: response?.data || response || null }));
    } catch (error) {
      setError(`Report failed for ${customerId}: ${error?.message}`);
    } finally {
      setReportLoading((prev) => ({ ...prev, [customerId]: false }));
    }
  };

  return (
    <Paper elevation={0} sx={{ maxWidth: 960, border: 1, borderColor: "divider", bgcolor: "background.paper", p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box className={classes.filterRow}>
        <TextField
          size="small"
          placeholder="Filter by customer, site, or serial…"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Report date"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        <Tooltip title="Refresh customer list">
          <IconButton size="small" sx={{ opacity: 0.55, "&:hover": { opacity: 1 } }} onClick={loadCustomers}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress />
        </Box>
      ) : filteredCustomers.length === 0 ? (
        <Alert severity="info">
          {customers.length === 0 ? "No customers configured." : "No results match your filter."}
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small" className={classes.tableAuto}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.th}>Serial</TableCell>
                <TableCell className={classes.th}>Site</TableCell>
                <TableCell className={classes.th}>Status</TableCell>
                <TableCell className={classes.th} sx={{ width: 70 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <CustomerRows
                  key={customer.id || customer.name}
                  customer={customer}
                  selectedDate={selectedDate}
                  analysisResults={analysisResults}
                  analysisLoading={analysisLoading}
                  onRun={runAnalysis}
                  reportResults={reportResults}
                  reportLoading={reportLoading}
                  onLoadReport={loadReport}
                  classes={classes}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
